import { useState, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../../lib/supabaseStorage';

export default function AdminSettings() {
  console.log('AdminSettings component rendered');
  console.log('Current URL:', window.location.href);
  const { settings, updateSettings, loading: settingsLoading } = useSettings();
  const [formData, setFormData] = useState(settings);
  const [logoPreview, setLogoPreview] = useState(settings.logo);
  const [faviconPreview, setFaviconPreview] = useState(settings.favicon);
  const [heroPreview, setHeroPreview] = useState(settings.heroBackgroundImage);
  const [saving, setSaving] = useState(false);

  // Update form when settings load
  useEffect(() => {
    if (!settingsLoading) {
      setFormData(settings);
      setLogoPreview(settings.logo);
      setFaviconPreview(settings.favicon);
      setHeroPreview(settings.heroBackgroundImage);
    }
  }, [settings, settingsLoading]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (file: File, type: 'logo' | 'favicon' | 'hero') => {
    try {
      setSaving(true);
      const imageUrl = await uploadImage(file, 'settings');
      console.log(`${type} uploaded to Supabase:`, imageUrl);
      
      if (type === 'logo') {
        setLogoPreview(imageUrl);
        handleChange('logo', imageUrl);
      } else if (type === 'favicon') {
        setFaviconPreview(imageUrl);
        handleChange('favicon', imageUrl);
      } else if (type === 'hero') {
        setHeroPreview(imageUrl);
        handleChange('heroBackgroundImage', imageUrl);
      }
      toast.success('تم رفع الصورة بنجاح!');
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      toast.error('فشل رفع الصورة. حاول مرة أخرى.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success('تم حفظ الإعدادات بنجاح!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('فشل حفظ الإعدادات. حاول مرة أخرى.');
    } finally {
      setSaving(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">إعدادات الموقع</h1>
          <p className="text-gray-600 mt-1">إدارة إعدادات وتخصيص الموقع الإلكتروني</p>
        </div>
        <Button 
          onClick={handleSave} 
          className="bg-[#D4AF37] hover:bg-[#B8960C] text-white"
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 ml-2 animate-spin" />
              جاري الحفظ...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 ml-2" />
              حفظ التغييرات
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto">
          <TabsTrigger value="general">عام</TabsTrigger>
          <TabsTrigger value="branding">العلامة التجارية</TabsTrigger>
          <TabsTrigger value="contact">التواصل</TabsTrigger>
          <TabsTrigger value="social">وسائل التواصل</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>المعلومات العامة</CardTitle>
              <CardDescription>معلومات أساسية عن الموقع</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="siteName">اسم الموقع بالعربية</Label>
                  <Input
                    id="siteName"
                    value={formData.siteNameArabic}
                    onChange={(e) => handleChange('siteNameArabic', e.target.value)}
                    placeholder="دار العقار"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">العنوان</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="القاهرة، مصر"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">وصف الموقع</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="منصة رائدة في مجال العقارات في مصر"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="metaTitle">عنوان الصفحة (SEO)</Label>
                <Input
                  id="metaTitle"
                  value={formData.metaTitle}
                  onChange={(e) => handleChange('metaTitle', e.target.value)}
                  placeholder="دار العقار - أفضل العقارات في مصر"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="metaDescription">وصف الصفحة (SEO)</Label>
                <Textarea
                  id="metaDescription"
                  value={formData.metaDescription}
                  onChange={(e) => handleChange('metaDescription', e.target.value)}
                  placeholder="اكتشف أفضل العقارات للبيع والإيجار في مصر"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Settings */}
        <TabsContent value="branding">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>الشعار (Logo)</CardTitle>
                <CardDescription>قم برفع شعار الموقع - يوصى باستخدام صورة PNG شفافة</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {logoPreview && (
                  <div className="flex justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed">
                    <img src={logoPreview} alt="Logo Preview" className="max-h-32 object-contain" />
                  </div>
                )}
                
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, 'logo');
                    }}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Label
                    htmlFor="logo-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-md cursor-pointer hover:bg-[#B8960C] transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    رفع الشعار
                  </Label>
                  {logoPreview && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setLogoPreview('');
                        handleChange('logo', '');
                      }}
                    >
                      إزالة الشعار
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>الأيقونة المفضلة (Favicon)</CardTitle>
                <CardDescription>قم برفع أيقونة الموقع - يوصى باستخدام صورة 32x32 أو 64x64 بكسل</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {faviconPreview && (
                  <div className="flex justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed">
                    <img src={faviconPreview} alt="Favicon Preview" className="w-16 h-16 object-contain" />
                  </div>
                )}
                
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, 'favicon');
                    }}
                    className="hidden"
                    id="favicon-upload"
                  />
                  <Label
                    htmlFor="favicon-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-md cursor-pointer hover:bg-[#B8960C] transition-colors"
                  >
                    <ImageIcon className="w-4 h-4" />
                    رفع الأيقونة
                  </Label>
                  {faviconPreview && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFaviconPreview('');
                        handleChange('favicon', '');
                      }}
                    >
                      إزالة الأيقونة
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>صورة الخلفية الرئيسية (Hero Background)</CardTitle>
                <CardDescription>قم برفع صورة الخلفية الرئيسية للصفحة الرئيسية - يوصى باستخدام صورة عالية الدقة</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {heroPreview && (
                  <div className="flex justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed">
                    <img src={heroPreview} alt="Hero Background Preview" className="max-h-32 object-contain" />
                  </div>
                )}
                
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, 'hero');
                    }}
                    className="hidden"
                    id="hero-upload"
                  />
                  <Label
                    htmlFor="hero-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-md cursor-pointer hover:bg-[#B8960C] transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    رفع الصورة
                  </Label>
                  {heroPreview && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setHeroPreview('');
                        handleChange('heroBackgroundImage', '');
                      }}
                    >
                      إزالة الصورة
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contact Settings */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>معلومات التواصل</CardTitle>
              <CardDescription>معلومات الاتصال التي ستظهر في الموقع</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">رقم الهاتف</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+20 123 456 789"
                    dir="ltr"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">البريد الإلكتروني</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="info@daraleqaar.com"
                    dir="ltr"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Media Settings */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle>روابط وسائل التواصل الاجتماعي</CardTitle>
              <CardDescription>أضف روابط صفحات الواصل الاجتماعي الخاصة بك</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="facebook">Facebook</Label>
                <Input
                  id="facebook"
                  value={formData.facebookUrl}
                  onChange={(e) => handleChange('facebookUrl', e.target.value)}
                  placeholder="https://facebook.com/yourpage"
                  dir="ltr"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="twitter">Twitter / X</Label>
                <Input
                  id="twitter"
                  value={formData.twitterUrl}
                  onChange={(e) => handleChange('twitterUrl', e.target.value)}
                  placeholder="https://twitter.com/yourpage"
                  dir="ltr"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instagram">Instagram</Label>
                <Input
                  id="instagram"
                  value={formData.instagramUrl}
                  onChange={(e) => handleChange('instagramUrl', e.target.value)}
                  placeholder="https://instagram.com/yourpage"
                  dir="ltr"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input
                  id="linkedin"
                  value={formData.linkedinUrl}
                  onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                  placeholder="https://linkedin.com/company/yourpage"
                  dir="ltr"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
